import { useEffect, useRef, useState } from "react";
import { Play, Pause } from "lucide-react";
import { toast } from "sonner";
import { BACKEND, PREVIEW_SECONDS, fmtTime } from "@/lib/releases";

export const PreviewPlayer = ({ src, title, accent, panel, onPlay }) => {
  const ref = useRef(null);
  const [playing, setPlaying] = useState(false);
  const [time, setTime] = useState(0);

  useEffect(() => () => ref.current?.pause(), []);

  const toggle = () => {
    const a = ref.current;
    if (!a) return;
    if (playing) return a.pause();
    if (a.currentTime >= PREVIEW_SECONDS) a.currentTime = 0;
    onPlay?.();
    a.play().catch(() => toast.error("Couldn't play the preview. Tap play again."));
  };

  const onTime = () => {
    const a = ref.current;
    if (a.currentTime >= PREVIEW_SECONDS) {
      a.pause();
      a.currentTime = 0;
      setTime(0);
      return;
    }
    setTime(a.currentTime);
  };

  const seek = (e) => {
    const a = ref.current;
    if (!a || !src) return;
    const r = e.currentTarget.getBoundingClientRect();
    a.currentTime = ((e.clientX - r.left) / r.width) * PREVIEW_SECONDS;
    setTime(a.currentTime);
  };

  const pct = (time / PREVIEW_SECONDS) * 100;
  return (
    <div className="flex items-center gap-4 rounded-2xl border border-white/10 p-3 pr-5 backdrop-blur-xl"
         style={{ background: panel }} data-testid="preview-player">
      {src && <audio ref={ref} src={`${BACKEND}${src}`} preload="metadata" onTimeUpdate={onTime}
                     onPlay={() => setPlaying(true)} onPause={() => setPlaying(false)} onEnded={() => setPlaying(false)} />}
      <button onClick={toggle} disabled={!src} aria-label={playing ? "Pause preview" : "Play preview"}
              className="shrink-0 w-14 h-14 rounded-full bg-white text-black flex items-center justify-center transition-transform duration-200 hover:scale-105 active:scale-95 disabled:opacity-40 disabled:hover:scale-100"
              data-testid="preview-play-btn">
        {playing ? <Pause size={20} fill="#000" /> : <Play size={20} fill="#000" className="ml-0.5" />}
      </button>
      <div className="flex-1 min-w-0">
        <div className="flex items-baseline justify-between gap-3">
          <div className="font-display font-bold text-sm truncate">{title}</div>
          <div className="font-mono text-[10px] tracking-[0.2em] text-white/50 shrink-0" data-testid="preview-time">
            {src ? `${fmtTime(time)} / ${fmtTime(PREVIEW_SECONDS)}` : "PREVIEW SOON"}
          </div>
        </div>
        <div onClick={seek} className="mt-2.5 h-1.5 rounded-full bg-white/15 cursor-pointer overflow-hidden" data-testid="preview-progress">
          <div className="h-full rounded-full transition-[width] duration-150" style={{ width: `${pct}%`, background: accent }} />
        </div>
        <div className="mt-2 font-mono text-[9px] tracking-[0.25em] text-white/40">30-SECOND PREVIEW</div>
      </div>
    </div>
  );
};
